import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Caption from '../../../../LuxKit/Typography/Caption'
import { endOfMonth } from 'lib/datetime/dateUtils'
import { getCalendarWeekStart, getCalendarWeekdayIndex, orderCalendarWeekdays } from 'lib/datetime/calendarUtils'
import { fillArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import DatePickerDay from './DatePickerDay'

const CalendarContainer = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

/* Exported so consumers can restyle the day grid (e.g. row sizing/gaps) via component selector */
export const CalendarDayGrid = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: repeat(7 , 1fr);
  align-content: center;
  justify-content: center;
  text-align: center;

  &.fixed-height {
    grid-template-rows: repeat(6, 1fr);
  }
`

const WeekHeader = styled(Caption)`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const EmptyDay = styled.div`
  min-height: ${rem(50)};
`

interface Props {
  className?: string
  // month and year this calendar is for
  month: number
  year: number
  onDayClick?: (day: Date) => void
  dayRender?: (day: Date) => React.ReactNode
  fixedHeight?: boolean
  hideWeekdays?: boolean
  distinguishWeekdays?: boolean
  /** Absolutely-positioned content rendered over the day grid (e.g. a range highlight) */
  gridOverlay?: React.ReactNode
}

function BasicCalendar(props: Props) {
  const intl = useIntl()
  const {
    className,
    month,
    year,
    dayRender,
    onDayClick = noop,
    fixedHeight = true,
    hideWeekdays = false,
    distinguishWeekdays,
    gridOverlay,
  } = props

  const [daysInMonth, emptyDays] = useMemo(() => {
    const monthDate = new Date(year, month)
    return [
      fillArray(endOfMonth(monthDate).getDate(), day => new Date(year, month, day + 1)),
      fillArray(getCalendarWeekdayIndex(monthDate, getCalendarWeekStart(intl.locale))),
    ]
  }, [intl.locale, month, year])

  return (
    <CalendarContainer className={className}>
      {!hideWeekdays && <CalendarWeekdayHeader distinguishWeekdays={distinguishWeekdays} variant={distinguishWeekdays ? 'large' : undefined} />}
      <CalendarDayGrid className={clsx({ 'fixed-height': fixedHeight })}>
        {emptyDays.map((val) => <EmptyDay key={val + 50} />)}
        {daysInMonth.map((day) => dayRender?.(day) ?? <DatePickerDay key={day.getTime()} date={day} onClick={onDayClick} />)}
        {gridOverlay}
      </CalendarDayGrid>
    </CalendarContainer>
  )
}

interface CalendarWeekdayHeaderProps {
  distinguishWeekdays?: boolean
  variant?: 'medium' | 'large'
  colour?: React.ComponentProps<typeof Caption>['colour']
}

export function CalendarWeekdayHeader(props: CalendarWeekdayHeaderProps) {
  const { variant = 'medium', colour = 'light-foreground-alt', distinguishWeekdays } = props
  const intl = useIntl()
  const sundayFull = intl.formatMessage({
    id: '89nt-Q8Sp19sQPvw2K0I',
    defaultMessage: 'Sunday',
    description: 'Full name of Sunday for calendar day header in the calendar picker',
  })
  const mondayFull = intl.formatMessage({
    id: 'o1NcO4LZJopRGZ3X0sUT',
    defaultMessage: 'Monday',
    description: 'Full name of Monday for calendar day header in the calendar picker',
  })
  const tuesdayFull = intl.formatMessage({
    id: 'h3GI4P3T36jh-bTzXqEF',
    defaultMessage: 'Tuesday',
    description: 'Full name of Tuesday for calendar day header in the calendar picker',
  })
  const wednesdayFull = intl.formatMessage({
    id: 'b3yvoMa_5LhO2DkZskED',
    defaultMessage: 'Wednesday',
    description: 'Full name of Wednesday for calendar day header in the calendar date picker',
  })
  const thursdayFull = intl.formatMessage({
    id: 'K-fgMbNtAW_VuYolibnk',
    defaultMessage: 'Thursday',
    description: 'Full name of Thursday for calendar day header in the calendar date picker',
  })
  const fridayFull = intl.formatMessage({
    id: 'tLzUG9xAlanmWTmf2r-7',
    defaultMessage: 'Friday',
    description: 'Full name of Friday for calendar day header in the calendar picker',
  })
  const saturdayFull = intl.formatMessage({
    id: 'K3pJwZgTLGOzXbllwDIU',
    defaultMessage: 'Saturday',
    description: 'Full name of Saturday for calendar day header in the calendar date picker',
  })
  const sundayAbbr = distinguishWeekdays ? intl.formatMessage({
    id: '5lzU7YuMrkIkeebP8mxp',
    defaultMessage: 'Su',
    description: 'Distinct abbreviation for Sunday in the flight calendar weekday header',
  }) : intl.formatMessage({
    id: 'wdgz4SbnuS9e21LLPdmm',
    defaultMessage: 'S',
    description: 'Single letter abbreviation for Sunday in calendar in the calendar date picker',
  })
  const mondayAbbr = intl.formatMessage({
    id: 'Cw_aF3ENy5NPlpmtbiOr',
    defaultMessage: 'M',
    description: 'Single letter abbreviation for Monday in calendar in the calendar date picker',
  })
  const tuesdayAbbr = distinguishWeekdays ? intl.formatMessage({
    id: 'uDr2_pdmPRY0Eiw__BpJ',
    defaultMessage: 'Tu',
    description: 'Distinct abbreviation for Tuesday in the flight calendar weekday header',
  }) : intl.formatMessage({
    id: 'ROBqIJNglwfvxCqDtBic',
    defaultMessage: 'T',
    description: 'Single letter abbreviation for Tuesday in calendar',
  })
  const wednesdayAbbr = intl.formatMessage({
    id: 'CHAZnvFqTF3btEjAncx9',
    defaultMessage: 'W',
    description: 'Single letter abbreviation for Wednesday in calendar',
  })
  const thursdayAbbr = distinguishWeekdays ? intl.formatMessage({
    id: 'TgUjkEy3qQSvpH6wfiEV',
    defaultMessage: 'Th',
    description: 'Distinct abbreviation for Thursday in the flight calendar weekday header',
  }) : intl.formatMessage({
    id: 'OZqJcTEyHpiZhKYw4L_n',
    defaultMessage: 'T',
    description: 'Single letter abbreviation for Thursday in calendar',
  })
  const fridayAbbr = intl.formatMessage({
    id: 'O7AvzNEBUk9c9doL6xdt',
    defaultMessage: 'F',
    description: 'Single letter abbreviation for Friday in calendar in the calendar date picker',
  })
  const saturdayAbbr = distinguishWeekdays ? intl.formatMessage({
    id: '4RnnZHHC8VJbqR7KQO9S',
    defaultMessage: 'Sa',
    description: 'Distinct abbreviation for Saturday in the flight calendar weekday header',
  }) : intl.formatMessage({
    id: 'ggcieMMmwoTE4xgtgDqe',
    defaultMessage: 'S',
    description: 'Single letter abbreviation for Saturday in calendar',
  })

  const weekdays = orderCalendarWeekdays([
    [sundayFull, sundayAbbr], [mondayFull, mondayAbbr], [tuesdayFull, tuesdayAbbr], [wednesdayFull, wednesdayAbbr], [thursdayFull, thursdayAbbr], [fridayFull, fridayAbbr], [saturdayFull, saturdayAbbr],
  ], getCalendarWeekStart(intl.locale))

  return (
    <WeekHeader variant={variant} align="center" colour={colour}>
      {weekdays.map(([full, abbr]) => <abbr key={full} title={full}>{abbr}</abbr>)}
    </WeekHeader>
  )
}

export default BasicCalendar
